import ClearIcon from '@mui/icons-material/Clear';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import {
  Box,
  Typography,
  styled,
} from "@mui/material";
import ActionButton from 'controls/global/action-button';
import PageSection from 'controls/global/page-section';
import ScrollToTopArrow from 'controls/global/scroll-to-top-arrow';
import React, {
  useEffect,
  useState,
} from "react";
import {
  FormProvider,
  useForm,
} from "react-hook-form";
import {
  gaps,
  margin,
  padding,
} from 'theme/defaultStyle';
import SearchIcon from 'theme/icons/SearchIcon';
import { useGlobalAccess } from 'utils/context/GlobalAccessContext';
import { useProfileSettingsCache } from 'utils/context/ProfileSettingsContext';
import AgencyField from './AgencyField';
import AgencyFieldHeader from './AgencyFieldHeader';
import GlobalAccessGrid from './GlobalAccessGrid';
import StateDropdown from './StateDropdown';
import { mapApiToUi } from './mapApitoUi';

const StyledBoxContainer = styled(Box)({
  position: "relative",
  "& .MuiOutlinedInput-input": {
    marginBottom: margin.xxsmall2,
    marginLeft: margin.small2,
    marginTop: margin.small,
    padding: `${padding.small} ${padding.small2}`,
  },
});

const StyledActionButton = styled(ActionButton)({
  height: "38px",
  width: "96px",
});

const SearchRow = styled("div")({
  display: "flex",
  gap: gaps.large1,
  "& > :nth-child(1)": {
    flex: "0 0 auto",
    width: "80px",
  },
  "& > :nth-child(2)": {
    flex: "1",
  },
  "& > :nth-child(3)": {
    flex: "0 0 auto",
  },
  "& > div": {
    display: "flex",
    gap: gaps.small3,
  },
});

const GlobalAccessHome = () => {
  const INITIAL_VALUES = {
    agency: "",
    state: "All",
  };

  const methods = useForm({ defaultValues: INITIAL_VALUES });
  const { reset, getValues } = methods;
  const [{ companyLocationStates }, { setShowChangeAgency }] = useGlobalAccess();
  const [{ profileSettings }] = useProfileSettingsCache();
  const [noRecordMessage, setNoRecordsMessage] = useState<string[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<any>([]);
  const [isClear, setIsClear] = useState(false);
  const [page, setPage] = useState(1);

  const allUserCompanies = companyLocationStates;
  const clearAgencyInput = () => {
    reset(INITIAL_VALUES);
    setNoRecordsMessage([""]);
    setIsClear(true);
    setPage(1);
  };

  const handleSearch = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPage(1);
    event.preventDefault();
    const userTypedValue = getValues("agency").trim();
    const userTypedValueLowerCase = userTypedValue.toLowerCase();
    const userSelectedValue = getValues("state");
    let filteredCompanies = [...allUserCompanies];

    if (userSelectedValue !== "All") {
      filteredCompanies = filteredCompanies.filter((company) => company.TerritoryStates?.includes(userSelectedValue));
    }

    if (userTypedValueLowerCase !== "") {
      filteredCompanies = filteredCompanies.filter((company) =>
        Object.values(company).some(
          (value) => typeof value === "string" && value.toLowerCase().includes(userTypedValueLowerCase)
        ),
      );
    }

    if (filteredCompanies.length === 0) {
      setNoRecordsMessage([
        `No matches found for ${userTypedValue}`,
        "Please adjust your search criteria and search again.",
      ]);
    } else {
      setNoRecordsMessage([""]);
    }
    setFilteredCompanies(mapApiToUi(filteredCompanies));
  };

  useEffect(() => {
    setFilteredCompanies(mapApiToUi([...allUserCompanies]));
    if (isClear) {
      setIsClear(false);
    }
  }, [isClear]);

  useEffect(() => {
    setShowChangeAgency(
      allUserCompanies?.length > 1 &&
      profileSettings?.userAgencyCount > 1 &&
      Boolean(profileSettings?.isStaffUser)
    );
  }, [allUserCompanies, profileSettings, setShowChangeAgency]);

  return (
    <StyledBoxContainer>
      <FormProvider {...methods}>
        <form noValidate>
          <PageSection
            icon={<InsertChartIcon />}
            title="Select an Agency"
            contentAlignment="beside-header"
          >
            <>
              <AgencyFieldHeader />
              <Typography className="subheading">Enter search criteria below to find an agency.</Typography>
              <SearchRow>
                <StateDropdown />
                <AgencyField />
                <div>
                  <StyledActionButton
                    variant="outlined"
                    color="secondary"
                    startIcon={<ClearIcon />}
                    onClick={clearAgencyInput}
                  >
                    Clear
                  </StyledActionButton>
                  <StyledActionButton
                    variant="outlined"
                    color="primary"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    type="submit"
                  >
                    Search
                  </StyledActionButton>
                </div>
              </SearchRow>
              <Typography className="subheading">{"Select an agency below to proceed."}</Typography>
              <GlobalAccessGrid
                filteredCompanies={filteredCompanies}
                noRecordMessage={noRecordMessage}
                setNoRecordsMessage={setNoRecordsMessage}
                page={page}
                setPage={setPage}
              />
            </>
          </PageSection>
        </form>
      </FormProvider>
      <ScrollToTopArrow />
    </StyledBoxContainer>
  );
};

export default GlobalAccessHome;
